import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const TruckFormModal = ({ isOpen, onClose, truck, onSuccess, language = 'pt-BR' }) => {
  const isEdit = !!truck;
  const [loading, setLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState({ registration: false, seguro: false });
  const [formData, setFormData] = useState({
    vin_number: '',
    placa: '',
    modelo: '',
    ano: new Date().getFullYear(),
    cor: '',
    quilometragem_atual: 0,
    fotos: [],
    documentos: {
      registration: { url: '', validade: '' },
      seguro: { url: '', validade: '' }
    }
  });

  // Preencher dados quando em modo edição
  React.useEffect(() => {
    if (truck && isOpen) {
      setFormData({
        vin_number: truck.vin_number || '',
        placa: truck.placa || '',
        modelo: truck.modelo || '',
        ano: truck.ano || new Date().getFullYear(),
        cor: truck.cor || '',
        quilometragem_atual: truck.quilometragem_atual || 0,
        fotos: truck.fotos || [],
        documentos: truck.documentos || {
          registration: { url: '', validade: '' },
          seguro: { url: '', validade: '' }
        }
      });
    } else if (!truck && isOpen) {
      setFormData({
        vin_number: '',
        placa: '',
        modelo: '',
        ano: new Date().getFullYear(),
        cor: '',
        quilometragem_atual: 0,
        fotos: [],
        documentos: {
          registration: { url: '', validade: '' },
          seguro: { url: '', validade: '' }
        }
      });
    }
  }, [truck, isOpen]);

  // Upload de foto para servidor
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/api/trucks/upload-image`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setFormData(prev => ({
        ...prev,
        fotos: [...prev.fotos, ...uploadedUrls]
      }));
      
      toast.success(`${files.length} ${language === 'pt-BR' ? 'fotos enviadas!' : 'photos uploaded!'}`);
    } catch (error) {
      console.error('Erro upload:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao enviar fotos' : 'Error uploading photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Upload de documento
  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;

    console.log(`📤 Uploading ${docType}:`, file.name);

    setUploadingDocs(prev => ({ ...prev, [docType]: true }));
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      console.log(`📤 Sending to ${API_URL}/api/trucks/upload-document`);
      
      const response = await axios.post(`${API_URL}/api/trucks/upload-document`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log(`✅ Upload response for ${docType}:`, response.data);
      
      setFormData(prev => ({
        ...prev,
        documentos: {
          ...prev.documentos,
          [docType]: {
            url: response.data.url,
            validade: prev.documentos[docType].validade,
            uploaded_at: new Date().toISOString()
          }
        }
      }));
      
      console.log(`✅ Document ${docType} saved with URL:`, response.data.url);
      
      toast.success(`${docType === 'registration' ? 'Registration' : 'Seguro'} ${language === 'pt-BR' ? 'enviado!' : 'uploaded!'}`);
    } catch (error) {
      console.error(`❌ Erro upload ${docType}:`, error);
      toast.error(language === 'pt-BR' ? 'Erro ao enviar documento' : 'Error uploading document');
    } finally {
      setUploadingDocs(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleSubmit = async () => {
    // Validação diferente para criar vs editar
    if (!isEdit) {
      // Ao criar, todos campos obrigatórios
      if (!formData.vin_number || !formData.placa || !formData.modelo || !formData.ano) {
        toast.error(language === 'pt-BR' ? 'Preencha VIN, placa, modelo e ano' : 'Fill VIN, plate, model and year');
        return;
      }
    } else {
      // Ao editar, só validar campos que foram preenchidos (não exigir VIN e placa se não mudaram)
      if (!formData.modelo || !formData.ano) {
        toast.error(language === 'pt-BR' ? 'Preencha modelo e ano' : 'Fill model and year');
        return;
      }
    }

    if (!formData.documentos.registration.url || !formData.documentos.seguro.url) {
      toast.error(language === 'pt-BR' ? 'Faça upload dos documentos obrigatórios' : 'Upload required documents');
      return;
    }

    if (!formData.documentos.registration.validade || !formData.documentos.seguro.validade) {
      toast.error(language === 'pt-BR' ? 'Preencha as datas de validade' : 'Fill expiry dates');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (isEdit) {
        await axios.put(`${API_URL}/api/trucks/${truck.id}`, formData, { headers });
        toast.success(language === 'pt-BR' ? '✅ Caminhão atualizado!' : '✅ Truck updated!');
      } else {
        await axios.post(`${API_URL}/api/trucks`, formData, { headers });
        toast.success(language === 'pt-BR' ? '✅ Caminhão cadastrado!' : '✅ Truck registered!');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(error.response?.data?.detail || (language === 'pt-BR' ? 'Erro ao salvar' : 'Save error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (language === 'pt-BR' ? '✏️ Editar Caminhão' : '✏️ Edit Truck') : (language === 'pt-BR' ? '➕ Novo Caminhão' : '➕ New Truck')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* VIN Number e Placa */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'VIN Number (Chassi)' : 'VIN Number (Chassis)'} *</Label>
              <Input 
                value={formData.vin_number}
                onChange={(e) => setFormData({...formData, vin_number: e.target.value.toUpperCase()})}
                placeholder="9BWZZZ377VT004251"
                disabled={isEdit}
                className="font-mono"
              />
            </div>
            
            <div>
              <Label>
                {language === 'pt-BR' ? 'Placa' : 'License Plate'} *
                {isEdit && <span className="text-xs text-gray-500 ml-1">(editável)</span>}
              </Label>
              <Input 
                value={formData.placa}
                onChange={(e) => setFormData({...formData, placa: e.target.value.toUpperCase()})}
                placeholder="ABC-1234"
              />
            </div>
          </div>

          {/* Modelo */}
          <div>
            <Label>{language === 'pt-BR' ? 'Modelo' : 'Model'} *</Label>
            <Input 
              value={formData.modelo}
              onChange={(e) => setFormData({...formData, modelo: e.target.value})}
              placeholder="Ford F-150"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Ano' : 'Year'} *</Label>
              <Input 
                type="number"
                value={formData.ano}
                onChange={(e) => setFormData({...formData, ano: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Cor' : 'Color'}</Label>
              <Input 
                value={formData.cor}
                onChange={(e) => setFormData({...formData, cor: e.target.value})}
                placeholder={language === 'pt-BR' ? 'Branco' : 'White'}
              />
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Quilometragem' : 'Mileage'} (km)</Label>
              <Input 
                type="number"
                value={formData.quilometragem_atual}
                onChange={(e) => setFormData({...formData, quilometragem_atual: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          {/* FOTOS */}
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">{language === 'pt-BR' ? '📸 Fotos do Caminhão' : '📸 Truck Photos'}</h4>
            
            <div className="border-2 border-dashed rounded-lg p-6 text-center mb-3">
              <input 
                type="file" 
                multiple 
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="truck-photo-upload"
                disabled={uploadingPhotos}
              />
              <label htmlFor="truck-photo-upload" className="cursor-pointer">
                {uploadingPhotos ? (
                  <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin" />
                ) : (
                  <Upload className="h-12 w-12 mx-auto text-gray-400" />
                )}
                <p className="text-sm font-semibold text-gray-700 mt-2">
                  {uploadingPhotos 
                    ? (language === 'pt-BR' ? 'Enviando...' : 'Uploading...')
                    : (language === 'pt-BR' ? 'Clique para enviar fotos' : 'Click to upload photos')
                  }
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt-BR' ? 'PNG, JPG até 5MB cada' : 'PNG, JPG up to 5MB each'}
                </p>
              </label>
            </div>

            {formData.fotos && formData.fotos.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {formData.fotos.map((foto, idx) => (
                  <div key={idx} className="relative group bg-gray-100 rounded overflow-hidden">
                    <img 
                      src={foto} 
                      alt={`Foto ${idx + 1}`} 
                      className="w-full h-24 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        const placeholder = parent.querySelector('.photo-placeholder');
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                    <div className="photo-placeholder absolute inset-0 flex items-center justify-center bg-gray-200" style={{display: 'none'}}>
                      <span className="text-gray-400 text-xs">📷</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        fotos: formData.fotos.filter((_, i) => i !== idx)
                      })}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DOCUMENTOS */}
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">{language === 'pt-BR' ? '📝 Documentos Obrigatórios' : '📝 Required Documents'}</h4>
            
            <div className="space-y-4">
              {/* Registration */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <Label className="text-sm font-semibold mb-2 block">📄 Registration *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input 
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload('registration', file);
                      }}
                      className="hidden"
                      id="registration-upload"
                      disabled={uploadingDocs.registration}
                    />
                    <label htmlFor="registration-upload" className="cursor-pointer">
                      <div className="border-2 border-dashed rounded p-4 text-center hover:bg-white transition">
                        {uploadingDocs.registration ? (
                          <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin mb-1" />
                        ) : (
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-1" />
                        )}
                        <p className="text-xs text-gray-600 font-semibold">
                          {uploadingDocs.registration
                            ? (language === 'pt-BR' ? 'Enviando...' : 'Uploading...')
                            : formData.documentos.registration.url 
                              ? '✅ Carregado' 
                              : (language === 'pt-BR' ? 'Upload PDF/Imagem' : 'Upload PDF/Image')
                          }
                        </p>
                      </div>
                    </label>
                  </div>

                  <div>
                    <Label className="text-xs">{language === 'pt-BR' ? 'Validade (Mês/Ano)' : 'Expiry (Month/Year)'} *</Label>
                    <Input 
                      type="month"
                      value={formData.documentos.registration.validade}
                      onChange={(e) => setFormData({
                        ...formData,
                        documentos: {
                          ...formData.documentos,
                          registration: {...formData.documentos.registration, validade: e.target.value}
                        }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Seguro */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <Label className="text-sm font-semibold mb-2 block">🛡️ {language === 'pt-BR' ? 'Seguro' : 'Insurance'} *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input 
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleDocumentUpload('seguro', file);
                      }}
                      className="hidden"
                      id="insurance-upload"
                      disabled={uploadingDocs.seguro}
                    />
                    <label htmlFor="insurance-upload" className="cursor-pointer">
                      <div className="border-2 border-dashed rounded p-4 text-center hover:bg-white transition">
                        {uploadingDocs.seguro ? (
                          <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin mb-1" />
                        ) : (
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-1" />
                        )}
                        <p className="text-xs text-gray-600 font-semibold">
                          {uploadingDocs.seguro
                            ? (language === 'pt-BR' ? 'Enviando...' : 'Uploading...')
                            : formData.documentos.seguro.url 
                              ? '✅ Carregado' 
                              : (language === 'pt-BR' ? 'Upload PDF/Imagem' : 'Upload PDF/Image')
                          }
                        </p>
                      </div>
                    </label>
                  </div>

                  <div>
                    <Label className="text-xs">{language === 'pt-BR' ? 'Validade (Mês/Ano)' : 'Expiry (Month/Year)'} *</Label>
                    <Input 
                      type="month"
                      value={formData.documentos.seguro.validade}
                      onChange={(e) => setFormData({
                        ...formData,
                        documentos: {
                          ...formData.documentos,
                          seguro: {...formData.documentos.seguro, validade: e.target.value}
                        }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading || uploadingPhotos || uploadingDocs.registration || uploadingDocs.seguro}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || uploadingPhotos || uploadingDocs.registration || uploadingDocs.seguro}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {language === 'pt-BR' ? 'Salvando...' : 'Saving...'}
              </>
            ) : (
              language === 'pt-BR' ? '💾 Salvar' : '💾 Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TruckFormModal;
